<template>
  <div class="box" :class="{'has-background-white-ter': unwired}">

    <div class="is-flex is-justify-content-space-between is-align-items-center"
         :class="{'is-clickable': !unwired}"
         @click="toggle">
      <div>
        <span class="has-text-weight-bold">{{ title }}</span>
        <span v-if="unwired" class="tag is-light ml-2">Not yet available</span>
        <span v-else-if="entries.length" class="tag is-info is-light ml-2">{{ entries.length }} selected</span>
      </div>
      <icon v-if="!unwired" :icon="expanded ? 'chevron-up' : 'chevron-down'"/>
    </div>

    <p v-if="unwired" class="has-text-grey is-size-7 mt-2">
      This report cannot show configured fields yet. It will be enabled in a later release.
    </p>

    <div v-if="expanded && !unwired" class="mt-4">

      <div v-if="coreFields.length" class="mb-4">
        <p class="has-text-weight-semibold is-size-7 has-text-grey mb-1">General</p>
        <field-checkbox
          v-for="field in coreFields"
          :key="field.key"
          v-bind="{reportKey, editable}"
          :entry="{source: 'core', key: field.key, label: field.label}"
          :label="field.label"/>
      </div>

      <div v-if="siteFields.length" class="mb-4">
        <p class="has-text-weight-semibold is-size-7 has-text-grey mb-1">{{ Naming.Site }} fields</p>
        <field-checkbox
          v-for="field in siteFields"
          :key="field.label"
          v-bind="{reportKey, editable}"
          :entry="{source: 'site_custom_field', label: field.label}"
          :label="field.label"
          :hint="`(${field.site_count} ${field.site_count === 1 ? Naming.Site.toLowerCase() : Naming.Sites.toLowerCase()})`"/>
      </div>

      <div v-if="dynamicStatuses.length" class="mb-4">
        <p class="has-text-weight-semibold is-size-7 has-text-grey mb-1">Statuses</p>
        <field-checkbox
          v-for="group in dynamicStatuses"
          :key="group.name"
          v-bind="{reportKey, editable}"
          :entry="{source: 'dynamic_status', name: group.name, ids: group.ids, label: 'Status'}"
          :label="group.name"
          :hint="`(${group.asset_type_count} ${Naming.Asset.toLowerCase()} types)`"/>
      </div>

      <div v-if="assetTypes.length">
        <p class="has-text-weight-semibold is-size-7 has-text-grey mb-1">{{ Naming.Asset }} type fields</p>
        <div v-for="assetType in assetTypes" :key="assetType.asset_type_id" class="asset-type">
          <div class="is-clickable is-flex is-align-items-center"
               @click="toggleAssetType(assetType.asset_type_id)">
            <icon :icon="isAssetTypeOpen(assetType.asset_type_id) ? 'chevron-up' : 'chevron-down'" class="mr-2"/>
            <span>{{ assetType.asset_type_name }}</span>
            <span class="has-text-grey is-size-7 ml-2">({{ assetType.fields.length }})</span>
          </div>
          <div v-if="isAssetTypeOpen(assetType.asset_type_id)" class="ml-4">
            <field-checkbox
              v-for="field in assetType.fields"
              :key="field.field_id"
              v-bind="{reportKey, editable}"
              :entry="{
                source: 'asset_type_custom_field',
                asset_type_id: assetType.asset_type_id,
                field_id: field.field_id,
                label: field.label
              }"
              :label="field.label"/>
          </div>
        </div>
      </div>

      <div v-if="orphans.length" class="mt-4">
        <p class="has-text-weight-semibold is-size-7 has-text-grey mb-1">Configured but no longer available</p>
        <div v-for="orphan in orphans" :key="orphan.key" class="is-flex is-align-items-center">
          <span class="has-text-grey">{{ orphan.label }}</span>
          <button v-if="editable" class="button is-small is-text ml-2" type="button" @click="removeOrphan(orphan.key)">
            Remove
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex'
import FieldCheckbox from './FieldCheckbox'
import {entryKey} from '@/store/modules/reportFields'

export default {

  components: {FieldCheckbox},

  props: {
    reportKey: {type: String, required: true},
    title: {type: String, required: true},
    catalogue: {type: Object, required: true},
    editable: {type: Boolean, default: false},
    unwired: {type: Boolean, default: false},
  },

  data: () => ({
    expanded: false,
    openAssetTypes: [],
  }),

  computed: {
    ...mapGetters('reportFields', [
      'config'
    ]),

    entries() {
      return this.config[this.reportKey] || []
    },

    nativeCore() {
      return (this.catalogue.native_core_fields || {})[this.reportKey] || []
    },

    coreFields() {
      return (this.catalogue.core_fields || []).filter(field => !this.nativeCore.includes(field.key))
    },

    siteFields() {
      return this.catalogue.site_custom_fields || []
    },

    dynamicStatuses() {
      return this.catalogue.dynamic_statuses || []
    },

    assetTypes() {
      return this.catalogue.asset_types || []
    },

    availableKeys() {
      const keys = new Set()

      this.coreFields.forEach(field => keys.add(entryKey({source: 'core', key: field.key})))
      this.siteFields.forEach(field => keys.add(entryKey({source: 'site_custom_field', label: field.label})))
      this.dynamicStatuses.forEach(group => keys.add(entryKey({source: 'dynamic_status', name: group.name})))
      this.assetTypes.forEach(assetType => assetType.fields.forEach(field => keys.add(entryKey({
        source: 'asset_type_custom_field',
        asset_type_id: assetType.asset_type_id,
        field_id: field.field_id
      }))))

      return keys
    },

    orphans() {
      return this.entries
        .filter(entry => !this.availableKeys.has(entryKey(entry)))
        .map(entry => ({key: entryKey(entry), label: entry.label}))
    },
  },

  methods: {
    ...mapMutations('reportFields', [
      'setReportConfig'
    ]),

    toggle() {
      if (this.unwired) {
        return
      }

      this.expanded = !this.expanded
    },

    isAssetTypeOpen(id) {
      return this.openAssetTypes.includes(id)
    },

    toggleAssetType(id) {
      this.openAssetTypes = this.isAssetTypeOpen(id)
        ? this.openAssetTypes.filter(open => open !== id)
        : [...this.openAssetTypes, id]
    },

    removeOrphan(key) {
      this.setReportConfig({
        reportKey: this.reportKey,
        entries: this.entries.filter(entry => entryKey(entry) !== key)
      })
    },
  },

}
</script>

<style scoped>
.asset-type {
  padding: 0.2rem 0;
}
</style>
